/**
 * Het bestemmingsplan op één punt (MA-50): wat er mag, hoe hoog, en welke
 * plannen er gelden.
 *
 * De bron is de WMS van ruimtelijke plannen bij PDOK. De WFS daarnaast geeft
 * 404; een `GetFeatureInfo` op de WMS geeft wel alles, in één verzoek voor alle
 * lagen tegelijk en zonder sleutel. De browser vraagt het zelf op - PDOK staat
 * al in de CSP voor de kaarttegels, en er hoeft niets in de database.
 *
 * Sinds de Omgevingswet (2024) heet dit het tijdelijk deel van het
 * omgevingsplan. Wat een gemeente daarna in het omgevingsplan zelf zet, staat in
 * het DSO en niet hier.
 */
import { z } from 'zod';

export const PLU_WMS = 'https://service.pdok.nl/kadaster/plu/wms/v1_0';

/**
 * De bestemmingsvlakken als kaarttegels. PDOK heeft hier alleen een WMS, geen
 * WMTS; MapLibre vult `{bbox-epsg-3857}` per tegel zelf in. De WMS tekent tot
 * schaal 1:50.000, en in WebMercator is dat vanaf z14 - op z13 komt er een lege
 * tegel terug, dus daaronder hoef je hem niet op te vragen.
 */
export const BESTEMMINGSPLAN_TEGELS =
  `${PLU_WMS}?service=WMS&version=1.3.0&request=GetMap&layers=enkelbestemming&styles=` +
  '&crs=EPSG:3857&bbox={bbox-epsg-3857}&width=256&height=256&format=image/png&transparent=true';
export const BESTEMMINGSPLAN_TEGELGROOTTE = 256;
export const BESTEMMINGSPLAN_MIN_ZOOM = 14;
export const BESTEMMINGSPLAN_ATTRIBUTIE =
  '&copy; <a href="https://www.kadaster.nl">Kadaster</a> / PDOK, ruimtelijke plannen';

/**
 * De legenda van de tegels: de bestemmingshoofdgroep zoals PDOK hem in
 * `bestemmingshoofdgroep` zet, met de kleur waarin de WMS hem tekent (de
 * standaard uit de SVBP 2012). Overgenomen uit PDOK's eigen legendaplaatje op
 * 15-10-2026; volgorde ook.
 */
export const BESTEMMINGSPLAN_LEGENDA = [
  { groep: 'agrarisch', naam: 'Agrarisch', kleur: '#EBF0D2' },
  { groep: 'agrarisch met waarden', naam: 'Agrarisch met waarden', kleur: '#D2E1A5' },
  { groep: 'bedrijf', naam: 'Bedrijf', kleur: '#B45FD2' },
  { groep: 'bedrijventerrein', naam: 'Bedrijventerrein', kleur: '#C8A0D7' },
  { groep: 'bos', naam: 'Bos', kleur: '#64AA2D' },
  { groep: 'centrum', naam: 'Centrum', kleur: '#FFC8BE' },
  { groep: 'cultuur en ontspanning', naam: 'Cultuur en ontspanning', kleur: '#FF3C82' },
  { groep: 'detailhandel', naam: 'Detailhandel', kleur: '#FFA096' },
  { groep: 'dienstverlening', naam: 'Dienstverlening', kleur: '#F091BE' },
  { groep: 'gemengd', naam: 'Gemengd', kleur: '#FFBE87' },
  { groep: 'groen', naam: 'Groen', kleur: '#28C846' },
  { groep: 'horeca', naam: 'Horeca', kleur: '#FF6923' },
  { groep: 'infrastructuur', naam: 'Infrastructuur', kleur: '#CDCDCD' },
  { groep: 'kantoor', naam: 'Kantoor', kleur: '#EBC3D7' },
  { groep: 'maatschappelijk', naam: 'Maatschappelijk', kleur: '#DC9B78' },
  { groep: 'natuur', naam: 'Natuur', kleur: '#82A591' },
  { groep: 'recreatie', naam: 'Recreatie', kleur: '#B9D746' },
  { groep: 'sport', naam: 'Sport', kleur: '#82C846' },
  { groep: 'tuin', naam: 'Tuin', kleur: '#C8D76E' },
  { groep: 'verkeer', naam: 'Verkeer', kleur: '#CDCDCD' },
  { groep: 'ontspanning en vermaak', naam: 'Ontspanning en vermaak', kleur: '#FF3C82' },
  { groep: 'water', naam: 'Water', kleur: '#AFCDE1' },
  { groep: 'wonen', naam: 'Wonen', kleur: '#FFFF00' },
  { groep: 'woongebied', naam: 'Woongebied', kleur: '#FFFFB4' },
  { groep: 'overig', naam: 'Overig', kleur: '#EBE1EB' },
] as const;
export type BestemmingsplanLegendaRegel = (typeof BESTEMMINGSPLAN_LEGENDA)[number];

/** Wat er in het popupje komt, in deze volgorde. */
const LAGEN = [
  'enkelbestemming',
  'dubbelbestemming',
  'functieaanduiding',
  'bouwaanduiding',
  'gebiedsaanduiding',
  'maatvoering',
  'bestemmingsplangebied',
] as const;

/**
 * Het verzoek voor één punt. Een kader van ruwweg een meter met het punt in de
 * middelste pixel: `GetFeatureInfo` kijkt naar die pixel, dus groter maken
 * levert alleen buurvlakken op.
 *
 * Standaard als `text/plain`: dezelfde velden, maar zonder geometrie. In JSON
 * stuurt PDOK de hele contour van elk plan mee - voor Frans Halsstraat 1,2 MB
 * en ~0,9 s, tegen 16 kB en ~0,25 s als tekst (gemeten 15-10-2026). Lees het
 * antwoord dan met `leesBestemmingsplanTekst`.
 */
export function bestemmingsplanUrl(
  lng: number,
  lat: number,
  formaat: 'tekst' | 'json' = 'tekst',
): string {
  const dLat = 0.000005;
  const dLng = dLat / Math.cos((lat * Math.PI) / 180);
  const lagen = LAGEN.join(',');
  const params = new URLSearchParams({
    service: 'WMS',
    version: '1.3.0',
    request: 'GetFeatureInfo',
    layers: lagen,
    query_layers: lagen,
    styles: '',
    // CRS:84 is lengte-breedte, ook in WMS 1.3.0; EPSG:4326 zou om moeten.
    crs: 'CRS:84',
    bbox: [lng - dLng, lat - dLat, lng + dLng, lat + dLat].join(','),
    width: '101',
    height: '101',
    i: '50',
    j: '50',
    info_format: formaat === 'tekst' ? 'text/plain' : 'application/json',
    feature_count: '50',
  });
  return `${PLU_WMS}?${params}`;
}

const VIEWER = 'https://www.ruimtelijkeplannen.nl/viewer/view?planidn=';

export const bestemmingSchema = z.object({
  naam: z.string(),
  /**
   * De PDOK-laag zonder achtervoegsel: `enkelbestemming`, `dubbelbestemming`,
   * `functieaanduiding`, `bouwaanduiding` of `gebiedsaanduiding`.
   */
  laag: z.string(),
  /**
   * De hoofdgroep in kleine letters (`wonen`, `gemengd`, `waarde`, `overige
   * zone`); daarop vertaalt de samenvatting, niet op de vrije naam.
   */
  groep: z.string().nullable(),
  /** Naar het artikel in de regels; null als het plan er geen noemt. */
  regels: z.string().nullable(),
  /**
   * Uit welk plan, met datum. Een oud plan en zijn opvolger kunnen op hetzelfde
   * punt allebei een bestemming geven; de lezer moet kunnen zien welke de
   * nieuwste is.
   */
  plan: z.string().nullable(),
  datum: z.string().nullable(),
});
export type Bestemming = z.infer<typeof bestemmingSchema>;

export const maatSchema = z.object({ naam: z.string(), waarde: z.string() });
export type Maat = z.infer<typeof maatSchema>;

export const planSchema = z.object({
  id: z.string(),
  naam: z.string(),
  /** Een ontwerp en het vastgestelde plan dat erop volgde delen hun dossier. */
  dossier: z.string().nullable(),
  /** `vastgesteld`, `ontwerp`, ... zoals het plan het zelf noemt. */
  planstatus: z.string(),
  /** Bijvoorbeeld "geheel onherroepelijk in werking". */
  dossierstatus: z.string(),
  datum: z.string().nullable(),
  /** Het plan in de viewer van ruimtelijkeplannen.nl. */
  link: z.string(),
});
export type Plan = z.infer<typeof planSchema>;

export const bestemmingsplanSchema = z.object({
  bestemmingen: z.array(bestemmingSchema),
  dubbelbestemmingen: z.array(bestemmingSchema),
  /** Functie-, bouw- en gebiedsaanduidingen bij elkaar. */
  aanduidingen: z.array(bestemmingSchema),
  maten: z.array(maatSchema),
  /** Nieuwste eerst. */
  plannen: z.array(planSchema),
});
export type Bestemmingsplan = z.infer<typeof bestemmingsplanSchema>;

const featureSchema = z.object({
  id: z.string().optional(),
  properties: z.record(z.string(), z.unknown()).nullable(),
});
const antwoordSchema = z.object({ features: z.array(featureSchema) });
type Feature = z.infer<typeof featureSchema>;

const tekst = (waarde: unknown): string => (typeof waarde === 'string' ? waarde.trim() : '');

/** De eerste van een kommalijst, of null. */
const eersteLink = (waarde: unknown): string | null =>
  tekst(waarde)
    .split(',')
    .map((deel) => deel.trim())
    .find((deel) => deel.startsWith('https://')) ?? null;

/**
 * `"maximum bouwhoogte (m)"="16"`; soms meer paren achter elkaar. De eenheid
 * staat tussen haakjes in de naam en gaat achter het getal.
 */
export function leesMaatvoering(waarde: string): Maat[] {
  const maten: Maat[] = [];
  for (const [, naam = '', getal = ''] of waarde.matchAll(/"([^"]+)"="([^"]*)"/g)) {
    const eenheid = /\s*\(([^)]+)\)\s*$/.exec(naam);
    maten.push(
      eenheid
        ? { naam: naam.slice(0, eenheid.index), waarde: `${getal} ${eenheid[1]}` }
        : { naam, waarde: getal },
    );
  }
  return maten;
}

/**
 * Het antwoord van PDOK naar wat het popupje toont. De laag staat vooraan in
 * het feature-id (`enkelbestemming.<uuid>`, `gebiedsaanduiding_vlak.<uuid>`).
 * Historische objecten vallen af; dubbele namen binnen één plan ook, want een
 * bestemming die twee vlakken raakt is voor de lezer één bestemming.
 */
export function leesBestemmingsplan(json: unknown): Bestemmingsplan {
  return bouwBestemmingsplan(antwoordSchema.parse(json).features);
}

/**
 * Het `text/plain`-antwoord van dezelfde `GetFeatureInfo`: per laag een kop
 * `Layer 'naam'`, per object `Feature <nr>:` en daaronder `veld = 'waarde'`.
 * `fuuid` draagt hetzelfde id als in JSON (`enkelbestemming.<uuid>`).
 */
export function leesBestemmingsplanTekst(antwoord: string): Bestemmingsplan {
  if (!antwoord.trimStart().startsWith('GetFeatureInfo results')) {
    throw new Error('PDOK gaf geen GetFeatureInfo-antwoord');
  }
  const features: Feature[] = [];
  let laag = '';
  let huidig: Record<string, string> | null = null;
  for (const regel of antwoord.split(/\r?\n/)) {
    const kop = /^Layer '([^']+)'/.exec(regel);
    if (kop) {
      laag = kop[1] ?? '';
      continue;
    }
    const object = /^\s+Feature (\S+):/.exec(regel);
    if (object) {
      huidig = {};
      features.push({ id: `${laag}.${object[1]}`, properties: huidig });
      continue;
    }
    const veld = /^\s+(\w+) = '(.*)'\s*$/.exec(regel);
    if (veld && huidig) huidig[veld[1] ?? ''] = veld[2] ?? '';
  }
  for (const feature of features) {
    const fuuid = feature.properties?.fuuid;
    if (typeof fuuid === 'string' && fuuid.includes('.')) feature.id = fuuid;
  }
  return bouwBestemmingsplan(features);
}

function bouwBestemmingsplan(features: Feature[]): Bestemmingsplan {
  const uitslag: Bestemmingsplan = {
    bestemmingen: [],
    dubbelbestemmingen: [],
    aanduidingen: [],
    maten: [],
    plannen: [],
  };
  const gezien = new Set<string>();
  const eenmaal = (sleutel: string) => !gezien.has(sleutel) && gezien.add(sleutel);

  for (const feature of features) {
    const p = feature.properties ?? {};
    if (tekst(p.historisch) === '1') continue;
    /* `gebiedsaanduiding_vlak` en dergelijke: het achtervoegsel zegt alleen iets over de vorm. */
    const laag = ((feature.id ?? '').split('.')[0] ?? '').replace(/_(vlak|lijn|punt)$/, '');
    const naam = tekst(p.naam);
    const regels = eersteLink(p.verwijzingnaartekst);
    const plangebied = tekst(p.plangebied) || null;
    const datum = tekst(p.datum) || null;

    if (laag === 'bestemmingsplangebied') {
      const id = tekst(p.identificatie);
      if (!id || !eenmaal(`plan:${id}`)) continue;
      uitslag.plannen.push({
        id,
        naam: naam || id,
        dossier: tekst(p.dossierid) || null,
        planstatus: tekst(p.planstatus),
        dossierstatus: tekst(p.dossierstatus),
        datum,
        link: `${VIEWER}${encodeURIComponent(id)}`,
      });
    } else if (laag === 'maatvoering') {
      for (const maat of leesMaatvoering(tekst(p.maatvoering))) {
        if (eenmaal(`maat:${maat.naam}=${maat.waarde}`)) uitslag.maten.push(maat);
      }
    } else if (naam) {
      const lijst =
        laag === 'enkelbestemming'
          ? uitslag.bestemmingen
          : laag === 'dubbelbestemming'
            ? uitslag.dubbelbestemmingen
            : uitslag.aanduidingen;
      if (eenmaal(`${laag}:${naam}:${plangebied}`)) {
        const groep =
          tekst(p.bestemmingshoofdgroep) ||
          tekst(p.gebiedsaanduidinggroep) ||
          tekst(p.functieaanduidinggroep) ||
          tekst(p.bouwaanduidinggroep);
        lijst.push({
          naam,
          laag,
          groep: groep.toLowerCase() || null,
          regels,
          plan: plangebied,
          datum,
        });
      }
    }
  }

  /* Het plangebied is een id; de naam staat bij het plan zelf. */
  const plannaam = new Map(uitslag.plannen.map((plan) => [plan.id, plan.naam]));
  const nieuwsteEerst = <T extends { datum: string | null }>(a: T, b: T) =>
    (b.datum ?? '').localeCompare(a.datum ?? '');
  for (const lijst of [uitslag.bestemmingen, uitslag.dubbelbestemmingen, uitslag.aanduidingen]) {
    for (const bestemming of lijst) {
      bestemming.plan = bestemming.plan ? (plannaam.get(bestemming.plan) ?? null) : null;
    }
    lijst.sort(nieuwsteEerst);
  }
  uitslag.plannen.sort(nieuwsteEerst);
  return uitslag;
}

/** Geen enkel plan op dit punt: buiten elk plangebied, of PDOK kent het niet. */
export function isLeegBestemmingsplan(plan: Bestemmingsplan): boolean {
  return (
    plan.plannen.length === 0 &&
    plan.bestemmingen.length === 0 &&
    plan.dubbelbestemmingen.length === 0
  );
}
